<template>
  <div class="add-to-bag" data-testid="add-to-bag">
    <Button width="full" @click="handleAddToBag">
      {{ buttonText }}
    </Button>
  </div>
</template>

<script>
import Button from "@/components/ButtonV3.vue";
import { mapGetters, mapState, mapActions } from "vuex";
import { getFeatureToggles } from "@/helpers/featureToggles.js";
import { unwrapNodes } from "@/helpers/graphql";

export default {
  name: "AddToBag",
  components: {
    Button,
  },
  props: {
    /** Use quick-look product/variant instead of the main PDP product. */
    isolated: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["select-size", "clicked"],
  data() {
    return {
      isVariantAddedToBag: false,
    };
  },
  computed: {
    ...mapState("product", [
      "selectedVariant",
      "product",
      "quickLookProduct",
      "quickLookSelectedVariant",
    ]),
    ...mapGetters("product", [
      "variant",
      "preOrder",
      "sellingPlanAllocation",
      "availableForPreOrder",
      "selectedPrebuyCampaign",
      "prebuySellingPlanAllocation",
      "availableForPrebuy",
      "isPreLaunch",
    ]),
    activeProduct() {
      return this.isolated ? this.quickLookProduct : this.product;
    },
    activeIsPreLaunch() {
      return this.activeProduct?.tags?.includes("pre-launch") || false;
    },
    activeSelectedVariant() {
      return this.isolated
        ? this.quickLookSelectedVariant
        : this.selectedVariant;
    },
    activeHidePreorder() {
      return this.activeProduct?.tags?.includes("hide-preorder") || false;
    },
    activePreOrder() {
      if (!this.isolated) return this.preOrder;

      const preorder = this.activeSelectedVariant?.randbPreorder?.value;
      if (!preorder) return false;

      try {
        return JSON.parse(preorder);
      } catch {
        return false;
      }
    },
    activeAvailableForPreOrder() {
      if (!this.isolated) return this.availableForPreOrder;
      if (this.activeHidePreorder || !this.activePreOrder) return false;

      const { enabled = false, limit = 0, reserved = 0 } = this.activePreOrder;
      const inventory = this.activeSelectedVariant?.quantityAvailable || 0;

      return enabled && inventory <= 0 && reserved < limit;
    },
    activeSelectedPrebuyCampaign() {
      if (!this.isolated) return this.selectedPrebuyCampaign;
      if (!this.activeSelectedVariant?.prebuyCampaigns?.value) return null;

      let campaigns;
      try {
        campaigns = JSON.parse(
          this.activeSelectedVariant.prebuyCampaigns.value
        );
      } catch {
        return null;
      }

      const inventory = this.activeSelectedVariant?.quantityAvailable || 0;
      if (!campaigns || inventory <= 0) return null;

      const currentMarketId = window.shopifyMarket?.id;
      if (!currentMarketId) return null;

      return campaigns[String(currentMarketId)] || null;
    },
    activeAvailableForPrebuy() {
      if (!this.isolated) return this.availableForPrebuy;
      if (this.activeHidePreorder) return false;

      return (
        !!this.activeSelectedPrebuyCampaign &&
        (this.activeSelectedVariant?.quantityAvailable || 0) > 0
      );
    },
    activePrebuySellingPlanAllocation() {
      if (!this.isolated) return this.prebuySellingPlanAllocation;

      const campaign = this.activeSelectedPrebuyCampaign;
      if (!campaign?.sellingPlanId) return null;

      const sellingPlanAllocations =
        unwrapNodes(
          this.activeSelectedVariant?.sellingPlanAllocations?.edges
        ) || [];
      const campaignPlanId = campaign.sellingPlanId.split("/").pop();

      return sellingPlanAllocations.find((allocation) => {
        const allocationPlanId = allocation.sellingPlan?.id.split("/").pop();
        return allocationPlanId === campaignPlanId;
      });
    },
    activeSellingPlanAllocation() {
      if (!this.isolated) return this.sellingPlanAllocation;
      if (!this.activePreOrder?.sellingPlanId) return null;

      const sellingPlanAllocations =
        unwrapNodes(
          this.activeSelectedVariant?.sellingPlanAllocations?.edges
        ) || [];

      return sellingPlanAllocations.find((allocation) => {
        return allocation.sellingPlan?.id === this.activePreOrder.sellingPlanId;
      });
    },
    activeIsPreOrder() {
      return this.activeAvailableForPreOrder || this.activeAvailableForPrebuy;
    },
    availableForSale() {
      if (this.isolated) {
        if (this.activeIsPreLaunch) return true;
        if (!this.activeSelectedVariant) return true;
        if (this.activeIsPreOrder) return true;
        return this.isVariantInStock(this.activeSelectedVariant);
      }

      if (
        this.isPreLaunch ||
        !this.selectedVariant ||
        this.availableForPreOrder ||
        this.availableForPrebuy
      ) {
        return true;
      }

      return this.isVariantInStock(this.selectedVariant);
    },
    waitlistVersion() {
      const { waitlistVersion = 0 } = getFeatureToggles();
      return waitlistVersion;
    },
    buttonText() {
      if (this.activeIsPreLaunch) {
        return "COMING SOON - NOTIFY ME WHEN AVAILABLE";
      }

      const states = {
        notSelected: !this.activeSelectedVariant,
        selectedVariant: this.activeSelectedVariant && this.availableForSale,
        addedToBag: this.isVariantAddedToBag,
        preOrder: this.availableForSale && this.activeIsPreOrder,
        outOfStock: this.activeSelectedVariant && !this.availableForSale,
      };

      const messages = {
        selectSize: "SELECT SIZE",
        addToBag: "ADD TO BAG",
        addedToBag: "ADDED TO BAG",
        preOrder: "PRE-ORDER",
        outOfStock:
          !this.isolated && this.waitlistVersion !== 0
            ? "JOIN THE WAITLIST"
            : "NOTIFY ME WHEN AVAILABLE",
      };

      switch (true) {
        case states.notSelected:
          return messages.selectSize;
        case states.addedToBag:
          return messages.addedToBag;
        case states.preOrder:
          return messages.preOrder;
        case states.selectedVariant:
          return messages.addToBag;
        case states.outOfStock:
          return messages.outOfStock;
        default:
          return "";
      }
    },
  },
  methods: {
    ...mapActions("cart", ["addCartItem", "setCartOpen"]),
    ...mapActions("add-to-bag", ["showCompactStockNotif", "openWaitlist"]),
    ...mapActions("product", ["setSelectedVariant"]),
    isVariantInStock(variant) {
      if (!variant?.availableForSale) {
        return false;
      }

      // Some product queries omit quantityAvailable; fall back to availableForSale.
      if (
        variant.quantityAvailable == null ||
        Number.isNaN(variant.quantityAvailable)
      ) {
        return true;
      }

      return variant.quantityAvailable > 0;
    },
    openStockNotification() {
      const variant =
        this.activeSelectedVariant || this.selectedVariant || this.variant;

      if (!variant) {
        return;
      }

      if (!this.isolated && !this.selectedVariant) {
        this.setSelectedVariant(variant);
      }

      return this.showCompactStockNotif({
        product: this.activeProduct,
        variant,
        show: true,
      });
    },
    getPreorderCartPayload() {
      const usePrebuySystem = this.activeAvailableForPrebuy;
      const useOldPreorderSystem =
        this.activeAvailableForPreOrder && !usePrebuySystem;

      const attributes = [];
      let sellingPlanId = null;

      if (usePrebuySystem) {
        const campaign = this.activeSelectedPrebuyCampaign;
        sellingPlanId = campaign?.sellingPlanId;

        if (campaign) {
          attributes.push(
            {
              key: "_locationId",
              value: campaign.locationId,
            },
            {
              key: "_shipping",
              value: campaign.estDispatchDate,
            },
            {
              key: "_sellingPlanName",
              value:
                this.activePrebuySellingPlanAllocation?.sellingPlan?.name || "",
            }
          );
        }
      } else if (useOldPreorderSystem) {
        sellingPlanId = this.activePreOrder.sellingPlanId;
        attributes.push({
          key: "_sellingPlanName",
          value: this.activeSellingPlanAllocation?.sellingPlan?.name,
        });
      }

      return {
        ...(attributes.length > 0 && { attributes }),
        ...(sellingPlanId && { sellingPlanId }),
      };
    },
    handleAddToBag() {
      if (this.activeIsPreLaunch) {
        return this.openStockNotification();
      }

      if (this.isolated) {
        if (!this.activeSelectedVariant) {
          this.$emit("select-size");
          return;
        }

        if (!this.availableForSale) {
          return this.showCompactStockNotif({
            product: this.activeProduct,
            variant: this.activeSelectedVariant,
            show: true,
          });
        }

        this.isVariantAddedToBag = true;

        this.addCartItem({
          productId: this.activeProduct.id,
          variantId: this.activeSelectedVariant.id,
          quantity: 1,
          ...this.getPreorderCartPayload(),
        });

        setTimeout(() => {
          this.isVariantAddedToBag = false;
        }, 1000);

        // Close Shop the Look first, then open the bag so journal + PDP
        // ProductCards get the same handoff (one panel at a time).
        this.$emit("clicked");
        this.$nextTick(() => {
          this.setCartOpen(true);
        });
        return;
      }

      if (!this.selectedVariant) {
        this.$emit("select-size");
        return;
      }

      if (
        !this.availableForSale &&
        !this.availableForPreOrder &&
        !this.availableForPrebuy
      ) {
        // Waitlist A/B test arms open the waitlist drawer instead of the
        // inline compact stock notification form.
        if (this.waitlistVersion !== 0) {
          return this.openWaitlist();
        }

        return this.openStockNotification();
      }

      this.isVariantAddedToBag = true;

      this.addCartItem({
        productId: this.product.id,
        variantId: this.selectedVariant.id,
        quantity: 1,
        ...this.getPreorderCartPayload(),
      });

      setTimeout(() => {
        this.isVariantAddedToBag = false;
      }, 1000);

      this.$emit("clicked");
      this.$nextTick(() => {
        this.setCartOpen(true);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.add-to-bag {
  margin-bottom: 0;
}

.add-to-bag :deep(.button) {
  border-radius: 4px;
}
</style>
